/**
 * ReadOnlyFunnelCanvas — Spec D D1b (§12.1): the read-only substrate the
 * analytics mode renders on (and U13/U3 version viewing will reuse in D2).
 *
 * Deliberately NOT the editor canvas: no imperative handle, no CommandManager,
 * no coordinator coupling — the graph arrives as plain props (the editor feeds
 * a snapshot of its live canvas; version viewing will feed version-scoped
 * graphs via the key-remount idiom). Reuses the SAME node/edge renderer maps,
 * switched to their read-only presentation via `data.mode = 'analytics'`.
 */
import React, { useMemo, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Node,
  Edge,
  Panel,
  PanOnScrollMode,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './ReadOnlyFunnelCanvas.css';
import { useTheme } from '@mui/material';
import type { CanvasAnalyticsOverlay } from '@modules/reactFlow/types/flow-canvas-types';
import type { FunnelInsightEdgeMetric } from '@modules/funnel-insights';
import type { AnalyticsBoundVariable } from '@modules/funnel-insights/types';

/** Canvas rework T8: minimap heat colors — same literals as the node frames. */
const MINIMAP_HEAT_COLORS: Record<string, string> = {
  low: '#2e7d32',
  medium: '#ed6c02',
  high: '#d32f2f',
};
import { ANALYTICS_CANVAS_TEST_IDS, useFunnelUnit } from '@modules/funnel-insights';
import { canvasNodeTypes, canvasEdgeTypes } from '@modules/reactFlow/components/shared/ReactFlowCanvas';
import {
  withEdgeAnalyticsData,
  withNodeAnalyticsData,
} from '@modules/reactFlow/components/shared/analyticsOverlayAttach';
import {
  ZOOM_MIN,
  ZOOM_MAX,
  PAN_ON_SCROLL,
  PAN_ON_SCROLL_MODE,
  PAN_ON_SCROLL_SPEED,
  ZOOM_ON_PINCH,
  ZOOM_ON_SCROLL,
  ATTRIBUTION_POSITION,
  GRID_STEP_SIZE as DEFAULT_GRID_STEP_SIZE,
} from '@modules/reactFlow/config/canvas-constraints';

export interface ReadOnlyFunnelCanvasProps {
  /** Plain graph snapshot — positions included. Never mutated. */
  nodes: Array<Record<string, unknown>>;
  edges: Array<Record<string, unknown>>;
  analyticsOverlay?: CanvasAnalyticsOverlay;
  /** Canvas rework T5: screenId → variables bound to it (banner chips). */
  screenBoundVariables?: Record<string, AnalyticsBoundVariable[]>;
  onNodeClick?: (screenId: string) => void;
  /** I-A: flow-loss stub click — opens the drop-off audience drawer. */
  onFlowLossClick?: (screenId: string) => void;
  onAnalyticsEdgeClick?: (edgeId: string, metric: FunnelInsightEdgeMetric) => void;
  gridStepSize?: number;
  className?: string;
}

export const ReadOnlyFunnelCanvas: React.FC<ReadOnlyFunnelCanvasProps> = ({
  nodes,
  edges,
  analyticsOverlay,
  screenBoundVariables,
  onNodeClick,
  onFlowLossClick,
  onAnalyticsEdgeClick,
  gridStepSize = DEFAULT_GRID_STEP_SIZE,
  className = '',
}) => {
  const theme = useTheme();
  // F2 (FT-AC-090): the minimap leak overview follows the page counting unit.
  const funnelUnit = useFunnelUnit();
  // T7: the encoding legend starts expanded; one click folds it to a «?» chip.
  const [legendCollapsed, setLegendCollapsed] = useState(false);
  // Report-mode declutter: zero-traffic screens hide by default; the toggle
  // brings them back. Editor mode is untouched — this component only renders
  // in analytics mode.
  const [showAllScreens, setShowAllScreens] = useState(false);

  const maxTransitionCount = useMemo(() => {
    const counts = Object.values(analyticsOverlay?.edgesByTransitionId ?? {})
      .map(metric => metric.transitionCount);
    return counts.length > 0 ? Math.max(...counts) : 0;
  }, [analyticsOverlay]);

  const analyticsNodes = useMemo<Node[]>(
    () => (nodes as unknown as Node[]).map(node => ({
      ...withNodeAnalyticsData(node, analyticsOverlay, {
        mode: 'analytics',
        // Edit affordances from the editor snapshot are stripped, not carried
        onNodeAction: undefined,
        variablesById: undefined,
        // T5: screen-level bound variables for the banner chips (NOT the
        // block-node variablesById stripped above — different mechanism).
        boundVariables: screenBoundVariables?.[node.id],
        // I-A: the drain stub's own click handler (stopPropagation inside).
        onFlowLossClick,
      }),
      selected: false,
      draggable: false,
      connectable: false,
      selectable: false,
    })),
    [nodes, analyticsOverlay, screenBoundVariables, onFlowLossClick],
  );

  const analyticsEdges = useMemo<Edge[]>(
    () => (edges as unknown as Edge[]).map(edge => ({
      ...withEdgeAnalyticsData(edge, analyticsOverlay, {
        mode: 'analytics',
        analyticsMaxTransitionCount: maxTransitionCount,
        onAnalyticsEdgeClick,
        onEdgeClick: undefined,
      }),
      selected: false,
      selectable: false,
    })),
    [edges, analyticsOverlay, maxTransitionCount, onAnalyticsEdgeClick],
  );

  // Zero-traffic screens (no overlay metric, or one with neither visitors nor
  // entries) clutter the report. Hidden by default; NEVER hide when the whole
  // scope has no traffic — an empty canvas would read as "no screens exist".
  const traffickedNodeIds = useMemo(() => {
    const metrics = analyticsOverlay?.nodesByScreenId ?? {};
    return new Set(
      (nodes as unknown as Node[])
        .filter(node => {
          const metric = metrics[node.id];
          return Boolean(metric && (metric.visitors > 0 || metric.entries > 0));
        })
        .map(node => node.id),
    );
  }, [nodes, analyticsOverlay]);
  const hiddenCount = analyticsNodes.length - traffickedNodeIds.size;
  const nodeFilterActive = !showAllScreens && traffickedNodeIds.size > 0 && hiddenCount > 0;

  const visibleNodes = useMemo<Node[]>(
    () => (nodeFilterActive
      ? analyticsNodes.filter(node => traffickedNodeIds.has(node.id))
      : analyticsNodes),
    [analyticsNodes, nodeFilterActive, traffickedNodeIds],
  );
  const visibleEdges = useMemo<Edge[]>(
    () => (nodeFilterActive
      // Prune dangling edges — an edge into a hidden node would point at nothing.
      ? analyticsEdges.filter(edge => traffickedNodeIds.has(edge.source) && traffickedNodeIds.has(edge.target))
      : analyticsEdges),
    [analyticsEdges, nodeFilterActive, traffickedNodeIds],
  );

  return (
    <ReactFlowProvider>
      <div
        className={`analytics-canvas-readonly ${className}`}
        data-testid={ANALYTICS_CANVAS_TEST_IDS.canvas}
        style={{ width: '100%', height: '100%' }}
      >
        <ReactFlow
          nodes={visibleNodes}
          edges={visibleEdges}
          nodeTypes={canvasNodeTypes}
          edgeTypes={canvasEdgeTypes}
          onNodeClick={(_, node) => onNodeClick?.(node.id)}
          nodesDraggable={false}
          nodesConnectable={false}
          nodesFocusable={false}
          edgesFocusable={false}
          elementsSelectable={false}
          selectionOnDrag={false}
          deleteKeyCode={null}
          fitView
          minZoom={ZOOM_MIN}
          maxZoom={ZOOM_MAX}
          panOnScroll={PAN_ON_SCROLL}
          panOnScrollMode={PAN_ON_SCROLL_MODE as PanOnScrollMode}
          panOnScrollSpeed={PAN_ON_SCROLL_SPEED}
          zoomOnScroll={ZOOM_ON_SCROLL}
          zoomOnPinch={ZOOM_ON_PINCH}
          zoomOnDoubleClick={false}
          attributionPosition={ATTRIBUTION_POSITION}
          onlyRenderVisibleElements={true}
        >
          <Controls
            showInteractive={false}
            style={{
              background: theme.palette.background.paper,
              borderColor: theme.palette.divider,
              boxShadow: theme.shadows[2],
              // @ts-expect-error CSS custom properties for button styling
              '--xy-controls-button-background-color': theme.palette.background.paper,
              '--xy-controls-button-background-color-hover': theme.palette.action.hover,
              '--xy-controls-button-color': theme.palette.text.primary,
              '--xy-controls-button-color-hover': theme.palette.text.primary,
              '--xy-controls-button-border-color': theme.palette.divider,
            }}
          />
          <MiniMap
            style={{
              background: theme.palette.background.paper,
              borderColor: theme.palette.divider,
            }}
            maskColor={theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'}
            // T8: the minimap doubles as the funnel's leak overview.
            nodeColor={(node) => {
              const metric = analyticsOverlay?.nodesByScreenId[node.id];
              const bucket = funnelUnit === 'people' ? metric?.heatBucketPeople : metric?.heatBucket;
              return MINIMAP_HEAT_COLORS[bucket ?? '']
                ?? (theme.palette.mode === 'dark' ? '#616161' : '#9e9e9e');
            }}
          />
          {/* Report declutter: zero-traffic screens hidden by default. The
              toggle renders only when the filter has something to hide. */}
          {(hiddenCount > 0 && traffickedNodeIds.size > 0) && (
            <Panel position="top-right" style={{ marginTop: 8, marginRight: 8 }}>
              <label
                data-testid={ANALYTICS_CANVAS_TEST_IDS.showAllScreensToggle}
                data-checked={showAllScreens}
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  background: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  padding: '4px 10px',
                  fontSize: 12,
                  color: theme.palette.text.secondary,
                  boxShadow: theme.shadows[1],
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                title={showAllScreens
                  ? 'Hide screens with no traffic in this window'
                  : `${hiddenCount} screen${hiddenCount === 1 ? '' : 's'} with no traffic in this window are hidden`}
              >
                <input
                  type="checkbox"
                  checked={showAllScreens}
                  onChange={(event) => setShowAllScreens(event.target.checked)}
                  style={{ margin: 0 }}
                />
                Show all screens{!showAllScreens ? ` (+${hiddenCount})` : ''}
              </label>
            </Panel>
          )}
          {/* T7 (CRO-AC-009): the encoding legend — report mode only. */}
          <Panel position="bottom-left" style={{ marginLeft: 48, marginBottom: 16 }}>
            <div
              data-testid={ANALYTICS_CANVAS_TEST_IDS.canvasLegend}
              data-collapsed={legendCollapsed}
              onClick={() => setLegendCollapsed((collapsed) => !collapsed)}
              style={{
                display: 'flex',
                gap: 14,
                alignItems: 'center',
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
                padding: legendCollapsed ? '4px 10px' : '5px 12px',
                fontSize: 11,
                color: theme.palette.text.secondary,
                boxShadow: theme.shadows[1],
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                userSelect: 'none',
              }}
              title={legendCollapsed ? 'Show the encoding legend' : 'Hide the legend'}
            >
              {legendCollapsed ? (
                <span style={{ fontWeight: 600 }}>?</span>
              ) : (
                <>
                  <span><span style={{ display: 'inline-block', width: 18, borderTop: `3px solid ${theme.palette.grey[600]}`, verticalAlign: 'middle', marginRight: 5 }} />forward</span>
                  <span><span style={{ display: 'inline-block', width: 18, borderTop: `3px dashed ${theme.palette.warning.main}`, verticalAlign: 'middle', marginRight: 5 }} />back</span>
                  <span><span style={{ display: 'inline-block', width: 18, borderTop: `3px dotted ${theme.palette.info.main}`, verticalAlign: 'middle', marginRight: 5 }} />jump</span>
                  <span>width = volume</span>
                  <span style={{ color: MINIMAP_HEAT_COLORS.high }}>⬇ −N = drop-off</span>
                  <span>frame = leak vs median</span>
                </>
              )}
            </div>
          </Panel>
          <Background variant={BackgroundVariant.Dots} gap={gridStepSize} size={1} />
        </ReactFlow>
      </div>
    </ReactFlowProvider>
  );
};
